import React, { useEffect, useState } from "react";
import { X, HelpCircle, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const mono = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const serif = { fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontWeight: 400 };
const img = (f) => `${process.env.PUBLIC_URL}/help/${f}`;

export const STEPS = [
  {
    title: "Pick a track",
    shot: "01-track-chips.jpg",
    body: [
      "The Live Board at the top lists today's live tracks, sorted by Minutes to Post. Amber = under 10 MTP, red = at post.",
      "Click a chip to focus the whole app on that track's next race. Clear All resets for the next pick. Bell On rings 2 minutes before post.",
    ],
  },
  {
    title: "One-Click loads everything",
    shot: "02-lineup-oneclick.jpg",
    body: [
      "Picking a chip runs One-Click: it loads live Daily Double prices, Morning Line and live odds, handicaps leg 1, fills leg 2 from the Daily Double probables and handicaps that too.",
      "Auto-refresh 30s (top of the DD Matrix) keeps odds and ranks current; untick it to freeze the board.",
    ],
  },
  {
    title: "Read the Matrix & lock a Key",
    shot: "03-dd-matrix.jpg",
    body: [
      "Both halves rank by IQ Power (high = value). The matrix shows each horse's Morning Line, live Win Odds (grey ML until the pool opens) and IQ Power; horses Cybertote can't price show No Rank and are left out.",
      "Click a left saddle to eliminate a horse. Click the small dot on a saddle to lock it as your Key horse; the Key follows into Exacta Overlays and the Trifecta.",
    ],
  },
  {
    title: "Build the Daily Double ticket",
    shot: "04-dd-ticket.jpg",
    body: [
      "Choose a structure (2×5, 3×4…) and a base. The ticket wheels your top IQ Power horses in each leg.",
      "Tap any horse tile to take it off this ticket only (red X). Combos and cost update instantly; Restore brings them back.",
    ],
  },
  {
    title: "Exacta & Trifecta tickets",
    shot: "05-exotics.jpg",
    body: [
      "Exacta: top IQ Power horses over the 3 lowest-priced Daily Double horses. Trifecta: your Key (or the most bet-down horse) boxed with 3–5 others.",
      "Tap tiles to remove horses per slot, same as the Daily Double ticket.",
    ],
  },
  {
    title: "Find Exacta overlays",
    shot: "06-exacta-overlays.jpg",
    body: [
      "Capture Now pulls live Exacta probables. Each cell shows Actual, Fair and Overlay %. Bright green = paying more than fair.",
      "Pick the threshold (10–40%), the $1/$2 base, and switch Key On/Off to un-dim the full matrix.",
    ],
  },
  {
    title: "Archive & track performance",
    shot: "07-performance.jpg",
    body: [
      "Hit Archive on any ticket to save it to Ticket History (stored in this browser).",
      "After the race, Score pulls official results and P&L. The Performance Report shows win rate, ROI and average ticket by wager type.",
    ],
  },
];

export const GLOSSARY = [
  ["IQ Power", "RacetrackIQ's value score: every horse starts at 50, then earns points for its Morning Line, its live win odds and how heavily it's bet in the Daily Double. Higher = better; it drives every ranking and ticket."],
  ["No Rank", "Cybertote has only a placeholder price for this horse, so it is left out of rankings and tickets."],
  ["Fair", "Rule-of-thumb exacta value: Win(1st) × (Win(2nd) + 1) × base."],
  ["Overlay %", "(Actual − Fair) ÷ Fair. Positive = the combo pays more than its odds suggest."],
];

const Step = ({ n, step, flash }) => (
  <section
    className={`pb-8 -mx-3 px-3 pt-3 rounded-lg scroll-mt-2 transition-colors duration-700 ${
      flash ? "bg-[#84cc16]/10 ring-1 ring-[#84cc16]/60" : "ring-0"
    }`}
    data-testid={`qs-step-${n}`}
  >
    <div className="flex items-baseline gap-3 mb-3">
      <span className="text-[11px] font-bold text-[#0a0a0a] bg-[#84cc16] rounded px-1.5 py-0.5" style={mono}>
        {String(n).padStart(2, "0")}
      </span>
      <h3 className="text-2xl text-[#f5ecd6]" style={serif}>{step.title}</h3>
    </div>
    <img
      src={img(step.shot)}
      alt={step.title}
      loading="lazy"
      className="w-full rounded-md border border-stone-700 mb-3"
      data-testid={`qs-shot-${n}`}
    />
    <ul className="space-y-2">
      {step.body.map((t, i) => (
        <li key={i} className="text-[12.5px] leading-6 text-stone-300 pl-3 border-l border-stone-700" style={mono}>
          {t}
        </li>
      ))}
    </ul>
  </section>
);

const Glossary = () => (
  <section className="pt-2 pb-10" data-testid="qs-glossary">
    <h3 className="text-2xl text-[#f5ecd6] mb-4" style={serif}>Mini glossary</h3>
    <dl className="space-y-3">
      {GLOSSARY.map(([term, def]) => (
        <div key={term} className="grid grid-cols-[88px_1fr] gap-3 text-[12px] leading-5" style={mono}>
          <dt className="text-[#84cc16] font-bold uppercase tracking-[0.12em]">{term}</dt>
          <dd className="text-stone-300">{def}</dd>
        </div>
      ))}
    </dl>
  </section>
);

const PdfButton = () => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { downloadQuickStartPdf } = await import("../lib/quickStartPdf");
      await downloadQuickStartPdf({ steps: STEPS, glossary: GLOSSARY, imgBase: process.env.PUBLIC_URL });
      toast.success("Quick Start manual downloaded");
    } catch (e) {
      toast.error(`Couldn't build the PDF: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      data-testid="quick-start-pdf-btn"
      className="mt-3 inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#84cc16] text-[#0a0a0a] text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-[#a3e635] disabled:opacity-60 transition-colors"
      style={mono}
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
      {busy ? "Building PDF…" : "Download PDF"}
    </button>
  );
};

export const QuickStartButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="quick-start-btn"
    className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#84cc16]/60 text-[#d9f99d] text-[11px] font-bold uppercase tracking-[0.18em] hover:bg-[#84cc16] hover:text-[#0a0a0a] transition-colors"
    style={mono}
  >
    <HelpCircle size={15} /> Quick Start
  </button>
);

export const QuickStartPanel = ({ open, onClose, focus }) => {
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!open || !focus?.step) return undefined;
    const el = document.querySelector(`[data-testid="qs-step-${focus.step}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlash(focus.step);
    const t = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [open, focus]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <aside
      aria-hidden={!open}
      data-testid="quick-start-panel"
      className={`fixed top-0 right-0 z-[60] h-full w-full sm:w-[520px] bg-[#0b0b0b]/95 backdrop-blur-xl border-l border-stone-700 shadow-[-24px_0_60px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out ${
        open ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
    >
      <header className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-stone-800">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#84cc16] mb-1" style={mono}>
            RacetrackIQ-Live! · Guide
          </div>
          <h2 className="text-3xl text-[#f5ecd6]" style={serif}>Quick Start</h2>
          <p className="text-[11px] text-stone-500 mt-1" style={mono}>
            7 steps from track pick to archived ticket. The board stays live behind this panel.
          </p>
          <PdfButton />
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="quick-start-close"
          className="p-2 rounded-full text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
          aria-label="Close Quick Start"
        >
          <X size={18} />
        </button>
      </header>
      <div className="h-[calc(100%-158px)] overflow-y-auto px-6 pt-6" data-testid="quick-start-scroll">
        {STEPS.map((s, i) => (
          <Step key={s.title} n={i + 1} step={s} flash={flash === i + 1} />
        ))}
        <Glossary />
      </div>
    </aside>
  );
};

const QS_EVENT = "racetrackiq:quickstart";
export const openQuickStart = (step) =>
  window.dispatchEvent(new CustomEvent(QS_EVENT, { detail: { step } }));

export const useQuickStartEvents = (onOpen) => {
  useEffect(() => {
    const h = (e) => onOpen(e.detail?.step ?? null);
    window.addEventListener(QS_EVENT, h);
    return () => window.removeEventListener(QS_EVENT, h);
  }, [onOpen]);
};

// Span (not button) so it can sit inside header toggle buttons.
export const PanelHelp = ({ step, label }) => {
  const go = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openQuickStart(step);
  };
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && go(e)}
      data-testid={`panel-help-${step}`}
      title={`How ${label || "this panel"} works — Quick Start step ${step}`}
      aria-label={`Open Quick Start step ${step}`}
      className="inline-flex items-center justify-center h-5 w-5 rounded-full border border-stone-600 text-stone-400 hover:border-[#84cc16] hover:text-[#84cc16] hover:scale-110 transition-[color,border-color,transform] cursor-pointer align-middle shrink-0"
    >
      <HelpCircle size={12} />
    </span>
  );
};
